import { useSiteMap } from '@gik/api/legal';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { PageSection } from '@gik/ui-legacy/gik/PageSection';
import React from 'react';
import { useEnvStore } from '../store/EnvStore';
import bemBlock from '../utils/bemBlock';
import { ErrorPageSection } from './Error/ErrorPageSection';
import { SitemapSectionView } from './SitemapSectionView';

interface GiveSitemapPageProps {
  sitePagesData?: unknown[];
}

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ');
}

// Handles both old format 'give/by/situation/...' and new format 'situation/...'
const SITUATION_PREFIXES = ['situation/', 'give/by/situation/'] as const;

function buildSituationLabel(url: string) {
  // Extract segments after 'situation/' (handles both 'give/by/situation/' and 'situation/')
  const match = url.match(/(?:give\/by\/)?situation\/(.+)/);
  if (!match) return null;
  const segments = match[1].split('/');
  // Capitalize and join with '-'
  const label = segments.map(capitalize).join('-');
  return `Situation: ${label}`;
}

function GiveSitemapComp({ sitePagesData }: GiveSitemapPageProps) {
  const bem = bemBlock('sitemap');
  const { data: swrData } = useSiteMap();
  const rawBaseUrl = useEnvStore.getState().FRONTEND_BASE_URL || '/';
  const frontendBaseUrl = rawBaseUrl.endsWith('/') ? rawBaseUrl : rawBaseUrl + '/';

  // Normalize data: always array of objects with url/title
  const data = React.useMemo(() => {
    const raw = sitePagesData ?? swrData ?? [];
    if (raw.length > 0 && typeof raw[0] === 'string') {
      return raw.map((url: string) => ({ url, title: undefined }));
    }
    return raw;
  }, [sitePagesData, swrData]);
  const situations = React.useMemo(() => {
    // Extract the correct part of the URL to avoid duplication
    // Supports both old format 'give/by/situation/...' (from site-pages) and new 'situation/...' (from site-map)
    return data
      .filter(item => typeof item.url === 'string' && SITUATION_PREFIXES.some(p => item.url.startsWith(p)))
      .map(page => {
        // Extract the situation name only (after the prefix)
        const prefix = SITUATION_PREFIXES.find(p => page.url.startsWith(p));
        const situationPath = page.url.slice(prefix.length); // Just the part after the prefix
        return {
          url: situationPath,
          title: buildSituationLabel(page.url),
        };
      });
  }, [data]);

  return (
    <PageSection className={bem()}>
      <div className="tw-flex tw-flex-wrap">
        <SitemapSectionView baseUrl={frontendBaseUrl} title="Give By" items={situations} prefix={'/situation'} />
      </div>
    </PageSection>
  );
}

export const GiveSitemapPage = withComponentErrorBoundary(GiveSitemapComp, {
  ErrorFallback: ErrorPageSection,
});
